import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import DynamicForm from "../components/DynamicForm";
import { Button, Card } from "@mui/material";
import { Chart } from "react-google-charts";
import { useDispatch, useSelector } from "react-redux";
import DashboardTable from "./DashboardTable";
import CONSTANTS, {
  PRODUCT_CATEGORY_FORM_ID,
  USER_GROUPS,
  adminCategoriesToFetch,
  userCategoriesToFetch,
} from "../constants/constants";
import { pushNotification } from "../redux/actions";
import {
  getAdminDashboardData,
  getUserDashboardData,
} from "../services/DashboardService";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Colors from "../theme/colors.json";
import Lottie from "lottie-react";
import DataLoader from "../animations/LoadingCube.json";
import PremiumChart from "./Charts/PremiumChart";
import ProductwisePremiumChart from "./Charts/ProductwisePremiumChart";
import ProposalChart from "./Charts/ProposalChart";
import CommissionStatusChart from "./Charts/CommissionStatusChart";
import ServiceManagerPerformanceChart from "./Charts/ServiceManagerPerformanceChart";
import UpcomingRenewals from "./Charts/UpcomingRenewals";
import { fetchRuntimeFormData } from "../services/FormService";
import { fetchAllUsers } from "../services/UserService";

const currentDate = new Date();
const startOfMonth = new Date(
  currentDate.getFullYear(),
  currentDate.getMonth(),
  1
);
const endOfMonth = new Date(
  currentDate.getFullYear(),
  currentDate.getMonth() + 1,
  0
);

const useStyles = makeStyles((theme) => ({
  mainContainerDashboard: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "3%",
    width: "100%",
    // background: "#e3e7ef",
    "@media (min-width: 2000px)": {
      padding: "3.3%  2% 0 2%",
    },
    "@media (min-width: 1500px) and (max-width: 2000px)": {
      padding: "3.3%  2% 0 2%",
    },
    "@media (min-width: 1000px) and (max-width: 1500px)": {
      padding: "4.3%  2% 0 2%",
    },
    "@media (min-width: 600px) and (max-width: 1000px)": {
      padding: "7%  2% 0 2%",
    },
    "@media (max-width: 600px)": {
      padding: "10%  2% 0 2%",
    },
  },

  dashboardChartsContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
    rowGap: "2%",
    flexWrap: "wrap",
    height: "auto",
  },

  dashboardTableSection: {
    width: "100%",
    marginBottom: "4%",
  },
  chartCard: {
    flex: "0 0 32.5%",
    // width: "32.5%",
    // height: "100%",
    overflow: "hidden",
    borderRadius: 10,
    background: Colors.WHITE_BACKGROUND,
    padding: 20,
    "@media (min-width: 1500px) and (max-width: 2000px)": {
      height: "18rem",
    },
    "@media (min-width: 1600px) and (max-width: 2000px)": {
      height: "20rem",
    },
    "@media (min-width: 600px) and (max-width: 1000px)": {
      flex: "0 0 49%",
    },
    "@media (max-width: 600px)": {
      flex: "0 0 99%",
    },
    height: "16rem",
  },
  tableHeadingContainer: {
    // margin: "2% 2% 0 2%",
    padding: "2% 2% 0 2%",
    display: "flex",
    justifyContent: "space-between",
  },
  tableContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    backgroundColor: Colors.WHITE_BACKGROUND,
    borderRadius: "0.6rem",
    "& .css-rorn0c-MuiTableContainer-root": {
      width: "unset",
    },
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "column",
  },
  commonBtnStyle: {
    textTransform: "capitalize",
  },
  dashboardTableHeading: {
    fontFamily: "Barlow",
    fontSize: "15px",
    fontWeight: "600",
    lineHeight: "18px",
    letterSpacing: "0em",
    textAlign: "left",
  },
  dashboardTableHeaderBtnSection: {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
  },
  endArrowIcon: {
    color: "#016BEB",
    fontSize: "2rem",
    position: "relative",
    top: "-45%",
  },
  downArrowIconContainer: {
    textAlign: "center",
  },
  tableLoader: {
    display: "flex",
    justifyContent: "center !important",
    alignItems: "center !important",
    width: "100%",
  },
  btnEvent: {
    pointerEvents: "none",
  },
}));

const categoryKeyMappings = {
  overall: { noOfProposals: "No of Proposals" },
  employee: { tdHeading: "Employee / Profit Center Name" },
  insurer: { tdHeading: "Insurer Name" },
  category: { tdHeading: "Product Name" },
};

const Dashboard = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userGroup } = useSelector((state: any) => ({
    userGroup: state.userGroup,
  }));
  const [monthData, setMonthData] = useState<any>();
  const [yearData, setYearData] = useState<any>();

  const [selectedButton, setSelectedButton] = useState("overall");
  const [isLoading, setIsLoading] = useState(false);

  const [years, setYears] = useState<any>([]);
  const [months, setMonths] = useState<any>([
    {
      title: "All",
      value: "all",
    },
    {
      title: "Jan",
      value: "1",
    },
    {
      title: "Feb",
      value: "2",
    },
    {
      title: "Mar",
      value: "3",
    },
    {
      title: "Apr",
      value: "4",
    },
    {
      title: "May",
      value: "5",
    },
    {
      title: "Jun",
      value: "6",
    },
    {
      title: "Jul",
      value: "7",
    },
    {
      title: "Aug",
      value: "8",
    },
    {
      title: "Sep",
      value: "9",
    },
    {
      title: "Oct",
      value: "10",
    },
    {
      title: "Nov",
      value: "11",
    },
    {
      title: "Dec",
      value: "12",
    },
  ]);

  const [productCategories, setProductCategories] = useState<any>([]);
  const [allUsers, setAllUsers] = useState<any>([]);

  function getCurrentAndPastYears() {
    const currentYear = new Date().getFullYear();
    const years: any = [];

    for (let i = 0; i < 6; i++) {
      const year = currentYear - i;
      years.push({ title: year.toString(), value: year.toString() });
    }

    setYears(years);
  }

  const MyLoader = () => (
    <div className={classes.tableLoader}>
      <Lottie animationData={DataLoader} loop={true} />
    </div>
  );

  const handleButtonClick = (buttonName) => {
    setIsLoading(true);
    setSelectedButton(buttonName);
  };

  const fetchDataForMonthAndYear = async () => {
    try {
      const currentYear = currentDate.getFullYear();
      const currentMonth = currentDate.getMonth() + 1; // Month is 0-based

      // Fetch data for the current month
      const monthStartDateString = `${currentYear}-${currentMonth
        .toString()
        .padStart(2, "0")}-01`;
      const monthEndDateString = endOfMonth.toISOString().split("T")[0];

      // Fetch data for the financial year (last year's April 1st to current year's March 31st)
      const financialYearStartDateString = `${currentYear - 1}-04-01`;
      const financialYearEndDateString = `${currentYear}-03-31`;

      // Fetch data for the current month
      let responseMonth = await getAdminDashboardData(
        monthStartDateString,
        monthEndDateString,
        selectedButton
      );

      if (responseMonth?.success) {
        const keysAndValuesMonth = categoryKeyMappings[selectedButton] || {};
        const updatedDataMonth = {
          ...responseMonth,
          additionalKeys: keysAndValuesMonth,
        };

        // Update month data
        setMonthData(updatedDataMonth);

        dispatch(
          pushNotification({
            isOpen: true,
            message: responseMonth.message,
            type: CONSTANTS.SUCCESS,
          })
        );
      } else {
        dispatch(
          pushNotification({
            isOpen: true,
            message: responseMonth.message,
            type: CONSTANTS.ERROR,
          })
        );
      }

      // Fetch data for the financial year
      let responseYear = await getAdminDashboardData(
        financialYearStartDateString,
        financialYearEndDateString,
        selectedButton
      );
      setIsLoading(false);
      if (responseYear?.success) {
        const keysAndValuesYear = categoryKeyMappings[selectedButton] || {};
        const updatedDataYear = {
          ...responseYear,
          additionalKeys: keysAndValuesYear,
        };

        // Update year data
        setYearData(updatedDataYear);

        dispatch(
          pushNotification({
            isOpen: true,
            message: responseYear.message,
            type: CONSTANTS.SUCCESS,
          })
        );
      } else {
        dispatch(
          pushNotification({
            isOpen: true,
            message: responseYear.message,
            type: CONSTANTS.ERROR,
          })
        );
      }
    } catch (error) {
      console.error(
        `Error fetching data for category ${selectedButton}:`,
        error
      );
      dispatch(
        pushNotification({
          isOpen: true,
          message: `Error fetching data for category ${selectedButton}`,
          type: CONSTANTS.ERROR,
        })
      );
    }
  };

  const getProductCategories = async () => {
    const response = await fetchRuntimeFormData(
      PRODUCT_CATEGORY_FORM_ID,
      0,
      10000
    );

    const filteredProductCategories = response?.data?.content?.map((item) => {
      return {
        title: item?.formData?.productCategory,
        value: item?.formData?.productCategory,
      };
    });

    setProductCategories(
      [
        {
          title: "All",
          value: "all",
        },
        ...(filteredProductCategories || []),
      ] || []
    );
  };

  const handleFetchAllUsers = async () => {
    const response = await fetchAllUsers();
    if (response?.data) {
      setAllUsers([
        {
          fullName: "All",
          userId: "all",
        },
        ...response?.data,
      ]);
    } else {
      setAllUsers([
        {
          fullName: "All",
          userId: "all",
        },
      ]);
    }
  };

  useEffect(() => {
    if (userGroup === USER_GROUPS.ADMIN) {
      handleFetchAllUsers();
    }
    getProductCategories();
    getCurrentAndPastYears();
  }, []);

  // useEffect(() => {
  //   fetchDataForMonthAndYear();
  // }, [selectedButton]);

  return (
    <div className={classes.mainContainerDashboard}>
      <div className={classes.dashboardChartsContainer}>
        <div className={classes.chartCard}>
          <ProductwisePremiumChart years={years} months={months} />
        </div>

        <div className={classes.chartCard}>
          <CommissionStatusChart years={years} months={months} />
        </div>
        <div className={classes.chartCard}>
          <PremiumChart years={years} productCategories={productCategories} />
        </div>
        <div className={classes.chartCard}>
          <ProposalChart years={years} months={months} allUsers={allUsers} />
        </div>

        <div className={classes.chartCard}>
          <ServiceManagerPerformanceChart years={years} months={months} />
        </div>
        <div className={classes.chartCard}>
          <UpcomingRenewals />
        </div>
      </div>

      {/* <div className={classes.dashboardTableSection}>
        <div className={classes.tableContainer}>
          <div className={classes.tableHeadingContainer}>
            <div className={classes.dashboardTableHeading}>
              Proposals Overview
            </div>
            <div className={classes.dashboardTableHeaderBtnSection}>
              <div className={classes.buttonContainer}>
                <Button
                  variant={
                    selectedButton === "overall" ? "contained" : "outlined"
                  }
                  color="primary"
                  onClick={() => handleButtonClick("overall")}
                  size="small"
                  disabled={isLoading}
                  className={`${classes.commonBtnStyle} ${
                    selectedButton === "overall" ? classes.btnEvent : ""
                  }`}
                >
                  Overall
                </Button>
                <div className={classes.downArrowIconContainer}>
                  {selectedButton === "overall" && (
                    <ArrowDropDownIcon className={classes.endArrowIcon} />
                  )}
                </div>
              </div>
              {userGroup === USER_GROUPS.ADMIN ? (
                <>
                  <div className={classes.buttonContainer}>
                    <Button
                      variant={
                        selectedButton === "insurer" ? "contained" : "outlined"
                      }
                      color="primary"
                      onClick={() => handleButtonClick("insurer")}
                      size="small"
                      disabled={isLoading}
                      className={`${classes.commonBtnStyle} ${
                        selectedButton === "insurer" ? classes.btnEvent : ""
                      }`}
                    >
                      Insurer Wise
                    </Button>
                    <div className={classes.downArrowIconContainer}>
                      {selectedButton === "insurer" && (
                        <ArrowDropDownIcon className={classes.endArrowIcon} />
                      )}
                    </div>
                  </div>
                  <div className={classes.buttonContainer}>
                    <Button
                      variant={
                        selectedButton === "employee" ? "contained" : "outlined"
                      }
                      color="primary"
                      onClick={() => handleButtonClick("employee")}
                      size="small"
                      disabled={isLoading}
                      className={`${classes.commonBtnStyle} ${
                        selectedButton === "employee" ? classes.btnEvent : ""
                      }`}
                    >
                      Member Wise
                    </Button>
                    <div className={classes.downArrowIconContainer}>
                      {selectedButton === "employee" && (
                        <ArrowDropDownIcon className={classes.endArrowIcon} />
                      )}
                    </div>
                  </div>
                  <div className={classes.buttonContainer}>
                    <Button
                      variant={
                        selectedButton === "category" ? "contained" : "outlined"
                      }
                      color="primary"
                      onClick={() => handleButtonClick("category")}
                      size="small"
                      disabled={isLoading}
                      className={`${classes.commonBtnStyle} ${
                        selectedButton === "category" ? classes.btnEvent : ""
                      }`}
                    >
                      Product Wise
                    </Button>
                    <div className={classes.downArrowIconContainer}>
                      {selectedButton === "category" && (
                        <ArrowDropDownIcon className={classes.endArrowIcon} />
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          </div>
          <DashboardTable
            monthlyProposalData={monthData}
            yearlyProposalData={yearData}
            containerHeight={"34vh"}
            loading={isLoading}
          />
        </div>
      </div> */}
    </div>
  );
};

export default Dashboard;
